// The SAME function the server will run, not a second implementation of it. A preview
// computed by a reimplementation can disagree with the split that actually happens, and the
// whole point of showing a preview is that it predicts the result. `src/backend/rules.ts`
// establishes this import direction: the repo tsconfig globs infra/ and metro resolves from
// the project root, so the relative path works for both tsc and the bundler.
import { splitText, type TextPart } from '../../infra/lambda/shared/longText';

export type { TextPart };

/**
 * The PURE half of attaching a document, kept apart from the half that talks to the network.
 *
 * This separation is not tidiness. These two functions were originally next to
 * `uploadTextAsset` in `textAsset.ts`, which statically imports `CognitoService` and the
 * deployment store — so importing them into the build path dragged Cognito and MMKV in with
 * them, and every web-app build test died at module load with "Cannot read properties of
 * undefined (reading 'getAllKeys')". Ten tests, and the real error was swallowed by the build
 * store's own catch, so the symptom was "hosting was never called".
 *
 * Slugifying a title must not require an auth stack. Anything that only transforms strings
 * belongs here; anything that needs a token belongs in `textAsset.ts`.
 */

/**
 * The server's own slug rule (`apps.ts:791`), applied here so the name the generated app was
 * told to use is the name the text is actually stored under. These two deriving the string
 * differently is a 404 that only shows up on a published page.
 */
export function textAssetName(titleOrName: string): string {
  return titleOrName.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * What the split will produce, shown BEFORE anything is stored.
 *
 * A wrong heading pattern is cheap to fix beforehand and tedious afterwards, and `splitText`
 * never throws — an unusable pattern falls back to length-chunking — so a preview always has
 * something to show rather than an error to explain.
 */
export function previewParts(text: string, headingPattern?: string): TextPart[] {
  return splitText(text, headingPattern);
}
